import {
  getFirstParamValueAsSet,
  getSingleParamValue,
  paramValuesMapToSearch,
  removeParamValue,
  searchToParamValuesMap,
  setParamValues,
  UrlSearch,
  UrlSearchProvider,
} from '@tableau/data-catalog-shared';
import { KeySet } from '@tableau/data-structures';
import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import * as React from 'react';
import { defaultMemoize } from 'reselect';

export function constructNewSearchForFieldSelection(search: string, fieldId: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const fields = paramValuesMap[CatalogUrlParamKeys.field];
  if (fields && fields.indexOf(fieldId) >= 0) {
    removeParamValue(paramValuesMap, CatalogUrlParamKeys.field, fieldId);
  } else {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.field, [fieldId]);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export function constructNewSearchWithClearedFieldSelection(search: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const fields = paramValuesMap[CatalogUrlParamKeys.field];
  if (fields) {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.field, []);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export function constructNewSearchForEmbeddedDatasourceSelection(search: string, embeddedDatasourceId: string): string {
  const paramValuesMap = searchToParamValuesMap(search);

  // If we're switching to a new embedded data source, clear the field filter.
  const embeddedDataSources = paramValuesMap[CatalogUrlParamKeys.embeddedDataSource];
  const currentEmbeddedDataSourceId = embeddedDataSources && embeddedDataSources.length > 0 ? embeddedDataSources[0] : null;
  if (currentEmbeddedDataSourceId !== embeddedDatasourceId) {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.field, []);
  }

  setParamValues(paramValuesMap, CatalogUrlParamKeys.embeddedDataSource, [embeddedDatasourceId]);
  return paramValuesMapToSearch(paramValuesMap);
}

export type WorkbookSubplaceParams = {
  readonly urlSearchValue: string;
  readonly selectedFields: KeySet;
  readonly selectedEmbeddedDatasourceId: string;
  readonly updateFieldSelection: (fieldId: string) => void;
  readonly clearFieldSelection: () => void;
  readonly updateEmbeddedDatasourceSelection: (embeddedDatasourceId: string) => void;
};

export type WorkbookSubplaceParamsProviderProps = {
  readonly children: (params: WorkbookSubplaceParams) => React.ReactNode;
};

export class WorkbookSubplaceParamsProvider extends React.Component<WorkbookSubplaceParamsProviderProps> {

  public render() {
    return (
      <UrlSearchProvider>
        {urlSearch =>
          <WorkbookSubplaceParamsProviderInternal urlSearch={urlSearch}>
            {this.props.children}
          </WorkbookSubplaceParamsProviderInternal>
        }
      </UrlSearchProvider>
    );
  }

}

type WorkbookSubplaceParamsProviderInternalProps = {
  readonly urlSearch: UrlSearch;
  readonly children: (params: WorkbookSubplaceParams) => React.ReactNode;
};

class WorkbookSubplaceParamsProviderInternal extends React.Component<WorkbookSubplaceParamsProviderInternalProps> {

  // tslint:disable: typedef
  private readonly memoizedGetFirstParamValueAsSet = defaultMemoize(getFirstParamValueAsSet);
  private readonly memoizedGetSingleParamValue = defaultMemoize(getSingleParamValue);
  // tslint:enable: typedef

  public render() {
    return this.props.children({
      urlSearchValue: this.props.urlSearch.value,
      selectedFields: this.memoizedGetFirstParamValueAsSet(this.props.urlSearch.value, CatalogUrlParamKeys.field),
      selectedEmbeddedDatasourceId: this.memoizedGetSingleParamValue(this.props.urlSearch.value, CatalogUrlParamKeys.embeddedDataSource),
      updateFieldSelection: this.updateFieldSelection,
      clearFieldSelection: this.clearFieldSelection,
      updateEmbeddedDatasourceSelection: this.updateEmbeddedDatasourceSelection
    });
  }

  private readonly clearFieldSelection = () => {
    const urlSearch = this.props.urlSearch;
    const newSearchWithClearedFieldSelection = constructNewSearchWithClearedFieldSelection(urlSearch.value);
    urlSearch.update(newSearchWithClearedFieldSelection);
  }

  private readonly updateFieldSelection = (fieldId: string) => {
    const urlSearch = this.props.urlSearch;
    const newSearch = constructNewSearchForFieldSelection(urlSearch.value, fieldId);
    urlSearch.update(newSearch);
  }

  private readonly updateEmbeddedDatasourceSelection = (embeddedDatasourceId: string) => {
    const urlSearch = this.props.urlSearch;
    const newSearch = constructNewSearchForEmbeddedDatasourceSelection(urlSearch.value, embeddedDatasourceId);
    urlSearch.update(newSearch);
  }
}
